import React, { useState, useEffect } from 'react';
import FourPillars from '../components/FourPillars';

const CustomerReturnsPage = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [activeSection, setActiveSection] = useState('');

  const slideBgStyle = {
    backgroundImage: 'linear-gradient(120deg, rgba(15,23,42,0.9), rgba(14,165,233,0.8)), url(/returns.png)',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
  };

  const StepIcon = ({ step }) => {
    const iconSize = 140;
    const common = {
      width: iconSize,
      height: iconSize,
      viewBox: '0 0 140 140',
      fill: 'none',
    };
    
    switch (step) {
      case 1:
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <rect x="35" y="45" width="70" height="50" rx="4" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M45 60h50M45 75h50" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <circle cx="50" cy="90" r="6" fill="currentColor"/>
            <circle cx="70" cy="90" r="6" fill="currentColor"/>
            <circle cx="90" cy="90" r="6" fill="currentColor"/>
          </svg>
        );
      case 2:
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <circle cx="70" cy="60" r="20" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M55 60l15 15 30-30" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
            <rect x="45" y="85" width="50" height="20" rx="2" stroke="currentColor" strokeWidth="3" fill="none"/>
          </svg>
        );
      case 3:
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <circle cx="45" cy="50" r="12" stroke="currentColor" strokeWidth="3" fill="none"/>
            <circle cx="95" cy="50" r="12" stroke="currentColor" strokeWidth="3" fill="none"/>
            <circle cx="45" cy="90" r="12" stroke="currentColor" strokeWidth="3" fill="none"/>
            <circle cx="95" cy="90" r="12" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M57 50h36M45 62v36M95 62v36M57 90h36" stroke="currentColor" strokeWidth="3"/>
            <circle cx="70" cy="70" r="8" fill="currentColor"/>
          </svg>
        );
      case 4:
        return (
          <svg {...common}>
            <rect x="20" y="20" width="100" height="100" rx="12" stroke="currentColor" strokeWidth="4" fill="none"/>
            <path d="M20 20 L30 30 M120 20 L110 30 M20 120 L30 110 M120 120 L110 110" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <rect x="35" y="35" width="70" height="70" rx="6" stroke="currentColor" strokeWidth="3" fill="none"/>
            <path d="M45 55h50M45 70h50M45 85h40" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
            <path d="M80 85l8 8 16-16" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
          </svg>
        );
      default:
        return null;
    }
  };

  const steps = [
    {
      step: 1,
      title: 'Returns Intake & Automated Sorting',
      description: 'Secure receiving with automated sorting by category and condition, initial quality assessment, and serialized tracking for complete visibility.',
    },
    {
      step: 2,
      title: 'Professional Grading & Evaluation',
      description: 'Condition grading, functionality testing, and market value assessment to determine optimal recovery channels for maximum value.',
    },
    {
      step: 3,
      title: 'Strategic Multi-Channel Routing',
      description: 'Route items to highest-value channels: refurbishment for resale, secondary markets, liquidation, or certified recycling based on condition.',
    },
    {
      step: 4,
      title: 'Processing & Financial Settlement',
      description: 'Complete processing, resale execution, and financial settlement with detailed reporting and documentation for your records.',
    },
  ];

  const goToStep = (index) => {
    setCurrentStep(index);
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        'transform-returns',
        'key-benefits',
        'industry-solutions',
        'documentation'
      ];
      
      const scrollPosition = window.scrollY + 150;
      
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = document.getElementById(sections[i]);
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(sections[i]);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setActiveSection(sectionId);
    }
  };

  return (
    <main className="services">
      <div className="container">
        <div className="sv-content-wrapper">
          <div className="sv-main-content">
            {/* Hero */}
            <section className="sv-hero">
              <div className="sv-eyebrow">Customer Returns Processing</div>
              <h1 className="sv-title">Customer Returns Processing</h1>
              <p className="sv-subtitle">Convert costly returns into recoverable assets with our streamlined intake, evaluation, and resale process that maintains brand protection.</p>
            </section>

            {/* 4-Step Process Carousel */}
            <section className="ti-steps-carousel">
              <div className="ti-carousel-slide-container">
                <div 
                  className="ti-carousel-slides" 
                  style={{ transform: `translateX(-${currentStep * 100}%)` }}
                >
                  {steps.map((step, index) => (
                    <div key={step.step} className="ti-carousel-slide" style={slideBgStyle}>
                      <div className="ti-carousel-icon">
                        <StepIcon step={step.step} />
                      </div>
                      <div className="ti-carousel-content">
                        <div className="ti-step-badge">STEP {step.step}</div>
                        <h3>{step.title}</h3>
                        <p>{step.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="ti-carousel-indicators">
                {steps.map((_, index) => (
                  <button
                    key={index}
                    className={`ti-carousel-indicator ${index === currentStep ? 'active' : ''}`}
                    onClick={() => goToStep(index)}
                    aria-label={`Go to step ${index + 1}`}
                  />
                ))}
              </div>
            </section>

            {/* Main Content Section */}
            <section id="transform-returns" className="sv-overview">
              <h2 className="sv-section-title">Transform Returns Into Recoverable Value</h2>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                Customer returns represent one of the largest cost centers in retail and ecommerce operations. The average return rate ranges from 20-30% for online sales, creating significant operational burden and capital tied up in returned inventory. Our comprehensive returns processing service transforms this challenge into an opportunity to recover value while maintaining brand protection.
              </p>
              <p style={{ fontSize: '18px', lineHeight: '1.7', color: '#334155', marginBottom: '24px' }}>
                Our streamlined process handles everything from initial intake through final disposition, routing each return to the highest-value channel based on condition, market data, and brand requirements. We maintain complete brand protection through controlled resale channels and MAP compliance while maximizing recovery rates.
              </p>
            </section>

            {/* Key Benefits */}
            <section id="key-benefits" className="sv-features">
              <h2 className="sv-section-title">Key Benefits</h2>
              <div className="sv-features-grid">
                <div className="sv-feature">
                  <h4>Streamlined Processing</h4>
                  <p>Fast, efficient returns intake with automated sorting, grading, and routing to maximize recovery value and reduce processing time.</p>
                </div>
                <div className="sv-feature">
                  <h4>Brand Protection</h4>
                  <p>Maintain brand integrity with controlled resale channels, MAP compliance, and strategic market routing that protects your brand reputation.</p>
                </div>
                <div className="sv-feature">
                  <h4>Multi-Channel Recovery</h4>
                  <p>Route returns to highest-value channels including refurbishment, secondary markets, liquidation, or certified recycling based on condition.</p>
                </div>
                <div className="sv-feature">
                  <h4>Complete Visibility</h4>
                  <p>Real-time tracking, detailed reporting, and analytics to understand return patterns and optimize your returns management strategy.</p>
                </div>
              </div>
            </section>

            {/* Industry Solutions */}
            <section id="industry-solutions" className="sv-overview">
              <h2 className="sv-section-title">Industry-Specific Solutions</h2>
              <ul className="sv-bullets sv-list">
                <li>Ecommerce – Handle high-volume online returns with automated processing and fast turnaround</li>
                <li>Retail – Process in-store returns with brand-compliant resale strategies</li>
                <li>Electronics – Specialized handling for tech returns with data security and refurbishment</li>
                <li>Apparel & Fashion – Condition-based routing for clothing and accessories with quality preservation</li>
                <li>Consumer Goods – Efficient processing for household items, appliances, and general merchandise</li>
              </ul>
            </section>

            {/* Documentation */}
            <section id="documentation" className="sv-overview">
              <h2 className="sv-section-title">Complete Documentation & Reporting</h2>
              <ul className="sv-bullets sv-list">
                <li>Returns Processing Reports</li>
                <li>Condition Grading Documentation</li>
                <li>Recovery Channel Analytics</li>
                <li>Financial Settlement Statements</li>
                <li>Brand Compliance Certificates</li>
                <li>Environmental Impact Reports</li>
              </ul>
            </section>

            {/* Four Pillars */}
            <FourPillars />

            {/* CTA */}
            <section className="sv-cta">
              <div className="sv-cta-inner">
                <h3>Ready to optimize your returns process?</h3>
                <p>Get a free assessment and discover how we can transform your returns into recoverable value.</p>
                <div className="sv-cta-actions">
                  <a className="btn btn-primary" href="tel:+18778739336">Call (877) 873-9336</a>
                  <a className="btn btn-secondary" href="mailto:info@globoflo.com">Email Us</a>
                </div>
              </div>
            </section>
          </div>

          {/* Quick Links Sidebar */}
          <aside className="sv-quick-links">
            <h3 className="sv-quick-links-title">Customer Returns Processing</h3>
            <nav className="sv-quick-links-nav">
              <ul>
                <li>
                  <button 
                    className={activeSection === 'transform-returns' ? 'active' : ''}
                    onClick={() => scrollToSection('transform-returns')}
                  >
                    Transform Returns Into Recoverable Value
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'key-benefits' ? 'active' : ''}
                    onClick={() => scrollToSection('key-benefits')}
                  >
                    Key Benefits
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'industry-solutions' ? 'active' : ''}
                    onClick={() => scrollToSection('industry-solutions')}
                  >
                    Industry-Specific Solutions
                  </button>
                </li>
                <li>
                  <button 
                    className={activeSection === 'documentation' ? 'active' : ''}
                    onClick={() => scrollToSection('documentation')}
                  >
                    Complete Documentation & Reporting
                  </button>
                </li>
              </ul>
            </nav>
          </aside>
        </div>
      </div>
    </main>
  );
};

export default CustomerReturnsPage;
